import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { IOperationResultNoValue } from '../../../../extViewmodels';
import { useAdminAccountSnapshotUpdateCustomerSuccessSpecialistMutation } from '../../../../queries/Admin/AccountSnapshot/useAdminAccountSnapshotUpdateCustomerSuccessSpecialistMutation';
import { AutoCompleteSearchField } from '../../../../web/components/autocomplete/AutoCompleteSearchField';
import { SearchIcon } from '../../../../web/components/svgs/icons/SearchIcon';
import { WarningIcon, WarningIconType } from '../../../../web/components/svgs/icons/WarningIcon';
import { Button } from '../../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { THEME } from '../../../../web/components-v2/Theme/theme';

const JUSTIFICATION_MAX = 1000;

interface IProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	snapshotId: string;
	companyName?: string;
	currentSpecialistId?: string;
	currentSpecialistName?: string;
}

function isUserResult(item: Api.IEntity | string | Api.IUser): item is Api.IUser {
	return typeof item === 'object' && item !== null && 'id' in item && ('firstName' in item || 'lastName' in item);
}

export function EditCustomerSuccessSpecialistDialog({
	open,
	onOpenChange,
	snapshotId,
	companyName,
	currentSpecialistId,
	currentSpecialistName,
}: IProps) {
	const [pickedUser, setPickedUser] = React.useState<Api.IUser | null>(null);
	const [justification, setJustification] = React.useState('');
	const [error, setError] = React.useState<string | null>(null);
	const [snapshotMissing, setSnapshotMissing] = React.useState(false);

	const mutation = useAdminAccountSnapshotUpdateCustomerSuccessSpecialistMutation();

	React.useEffect(() => {
		if (open) {
			setPickedUser(null);
			setJustification('');
			setError(null);
			setSnapshotMissing(false);
			mutation.reset();
		}
		// mutation is stable; including it would re-run the reset on each render
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const trimmedJustification = justification.trim();
	const justificationOver = justification.length > JUSTIFICATION_MAX;
	const sameAsCurrent = !!pickedUser && !!currentSpecialistId && pickedUser.id === currentSpecialistId;
	const canSubmit =
		!!pickedUser && trimmedJustification.length > 0 && !justificationOver && !sameAsCurrent && !mutation.isPending;

	const handleSubmit = () => {
		if (!canSubmit || !pickedUser) {
			return;
		}
		setError(null);
		setSnapshotMissing(false);
		mutation.mutate(
			{
				id: snapshotId,
				userId: pickedUser.id,
				justification: trimmedJustification,
			},
			{
				onSuccess: () => {
					onOpenChange(false);
				},
				onError: (err: IOperationResultNoValue) => {
					if (err?.systemCode === 404) {
						setSnapshotMissing(true);
						return;
					}
					const raw = err?.systemMessage?.trim();
					// Only surface server messages that look like short, user-facing copy;
					// otherwise show a generic fallback so we don't leak exception/internal text.
					const looksUserFacing =
						!!raw && raw.length <= 200 && !/\b(at\s+\w+|\bException|stack|null reference)\b/i.test(raw);
					setError(looksUserFacing ? raw! : 'Failed to update Customer Success specialist. Please try again.');
				},
			}
		);
	};

	const handleAutocompleteSelected = (
		item: Api.IEntity | string | Api.IUser,
		type: Api.ResourceAutoCompleteViewModelType
	) => {
		if (type === Api.ResourceAutoCompleteViewModelType.User && isUserResult(item)) {
			setPickedUser(item);
		}
	};

	const counterColor = justificationOver ? s.characterCountOver : undefined;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[s.dialogContent]}>
				<DialogHeader>
					<DialogTitle>Edit Customer Success specialist</DialogTitle>
				</DialogHeader>

				<DialogBody styles={[s.dialogBody]}>
					<div className={css(s.warningBanner)}>
						<div className={css(s.warningIconWrap)}>
							<WarningIcon type={WarningIconType.Red} />
						</div>
						<div>
							<div className={css(s.warningTitle)}>Heads up — this is an audited change</div>
							<div className={css(s.warningBody)}>
								Changing the specialist sends a billing audit email to leadership with your name, the previous and new
								specialist, and your justification. This should only be done by managers in rare circumstances. Make
								sure the justification is clear — auditors may follow up.
							</div>
						</div>
					</div>

					{!!companyName && (
						<div className={css(s.contextLine)}>
							Account: <strong>{companyName}</strong>
						</div>
					)}

					<div className={css(s.fieldGroup)}>
						<div className={css(s.label)}>Current specialist</div>
						<div className={css(s.currentValue)}>{currentSpecialistName || 'Unassigned'}</div>
					</div>

					<div className={css(s.fieldGroup)}>
						<label htmlFor='cs-specialist-search' className={css(s.label)}>
							New specialist *
						</label>
						{pickedUser ? (
							<div className={css(s.pickedUserRow)}>
								<span>
									{pickedUser.firstName} {pickedUser.lastName}
								</span>
								<Button variant='ghost' size='sm' onClick={() => setPickedUser(null)}>
									Change
								</Button>
							</div>
						) : (
							<AutoCompleteSearchField
								anchorClassName={css(s.searchAnchor)}
								clearSearchFieldAfterSelectingItem={true}
								dropdownContentClassName={css(s.searchDropdown)}
								inputId='cs-specialist-search'
								inputProps={{
									placeholder: 'Search Customer Success specialist…',
									style: { flex: 1, minWidth: 0, width: '100%' },
								}}
								leftAccessory={
									<span className={css(s.iconSpan)}>
										<SearchIcon fillColor={THEME.colors.border.main} />
									</span>
								}
								onItemSelected={handleAutocompleteSelected}
								resultsLimit={5}
								type={Api.ResourceAutoCompleteViewModelType.User}
							/>
						)}
						{sameAsCurrent && <div className={css(s.helperWarn)}>That's already the assigned specialist.</div>}
					</div>

					<div className={css(s.fieldGroup)}>
						<label htmlFor='cs-justification' className={css(s.label)}>
							Justification *
						</label>
						<TextArea
							id='cs-justification'
							value={justification}
							onChange={e => setJustification(e.target.value)}
							placeholder='Why is this change being made? Auditors may follow up.'
							rows={4}
							maxLength={JUSTIFICATION_MAX + 50}
							disabled={mutation.isPending}
						/>
						<div className={css(s.characterCount, counterColor)}>
							{justification.length} / {JUSTIFICATION_MAX}
						</div>
					</div>

					{snapshotMissing && (
						<div className={css(s.errorMessage)}>
							This snapshot was deleted. Refresh the snapshots list to see the latest state.
						</div>
					)}
					{error && !snapshotMissing && <div className={css(s.errorMessage)}>{error}</div>}
				</DialogBody>

				<DialogFooter>
					<Button variant='secondary' onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
						Cancel
					</Button>
					<Button variant='destructive' onClick={handleSubmit} disabled={!canSubmit} isLoading={mutation.isPending}>
						Submit Change
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
}

const s = StyleSheet.create({
	dialogContent: {
		maxWidth: 560,
		maxHeight: '90vh',
		overflow: 'auto',
	},
	dialogBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	warningBanner: {
		display: 'flex',
		gap: THEME.spacing.sm + 4,
		padding: THEME.spacing.sm + 4,
		backgroundColor: THEME.colors.danger.light,
		border: `1px solid ${THEME.colors.danger.main}`,
		borderRadius: THEME.common.borderRadius,
	},
	warningIconWrap: {
		flexShrink: 0,
		width: 36,
		height: 36,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	warningTitle: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.danger.dark,
		marginBottom: THEME.spacing.xs,
	},
	warningBody: {
		fontSize: THEME.typography.fontSizes.xs + 1,
		lineHeight: 1.5,
		color: THEME.colors.text.main,
	},
	contextLine: {
		fontSize: THEME.typography.fontSizes.xs + 1,
		color: THEME.colors.text.main,
	},
	fieldGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs + 2,
	},
	label: {
		fontSize: THEME.typography.fontSizes.xs + 1,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	currentValue: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
	},
	pickedUserRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.sm + 4,
		paddingTop: THEME.spacing.sm,
		paddingBottom: THEME.spacing.sm,
		paddingLeft: THEME.spacing.sm + 4,
		paddingRight: THEME.spacing.sm + 4,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		fontSize: THEME.typography.fontSizes.sm,
	},
	searchAnchor: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		paddingLeft: THEME.spacing.sm,
		paddingRight: THEME.spacing.sm,
		minHeight: 36,
	},
	searchDropdown: {
		zIndex: 1000,
	},
	iconSpan: {
		display: 'flex',
		alignItems: 'center',
		marginRight: THEME.spacing.xs + 2,
	},
	helperWarn: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.danger.dark,
	},
	characterCount: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		marginTop: THEME.spacing.xs,
		alignSelf: 'flex-end',
	},
	characterCountOver: {
		color: THEME.colors.danger.dark,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	errorMessage: {
		padding: THEME.spacing.sm + 4,
		backgroundColor: THEME.colors.danger.light,
		border: `1px solid ${THEME.colors.danger.main}`,
		borderRadius: THEME.common.borderRadius,
		color: THEME.colors.danger.dark,
		fontSize: THEME.typography.fontSizes.sm,
	},
});
